<template>
  <div class="dashboard-mobile">
    <!-- Cabeçalho -->
    <header class="header">
      <h1 class="titulo">🕓 Controle de Ponto</h1>
    </header>

    <!-- Status -->
    <div class="status-card" v-if="statusAtual">
      <div class="status-info">
        <span class="label">Status atual</span>
        <span :class="['valor', statusClass]">{{ statusAtual }}</span>
      </div>

      <div v-if="entradaDoDia" class="status-info">
        <span class="label">Entrada</span>
        <span class="valor">{{ entradaDoDia }}</span>
      </div>
    </div>

    <!-- Botão principal -->
    <div class="acoes">
      <button class="btn-principal" @click="mostrarOpcoes = true">
        Marcar Ponto
      </button>
    </div>

    <!-- MODAL -->
    <transition name="fade">
      <div v-if="mostrarOpcoes" class="modal-overlay" @click.self="mostrarOpcoes = false">
        <div class="modal-content">
          <button class="modal-close" @click="mostrarOpcoes = false">✕</button>
          <h3 class="modal-title">Registrar Ponto</h3>

          <div class="campo">
            <label>Tipo de Registro</label>
            <select v-model="tipo" class="input">
              <option disabled value="">Selecione...</option>
              <option value="entrada">Entrada</option>
              <option value="pausa">Pausa</option>
              <option value="saida">Saída</option>
            </select>
          </div>

          <div class="campo">
            <label>Observação (opcional)</label>
            <textarea v-model="observacao" class="input" rows="2"></textarea>
          </div>

          <!-- Câmera + contador -->
          <div v-if="showCamera" class="camera-box">
            <video ref="video" autoplay playsinline></video>
            <div v-if="contador > 0" class="contador">{{ contador }}</div>
          </div>

          <!-- Prévia da selfie -->
          <div v-if="previewSelfie" class="preview">
            <img :src="previewSelfie" alt="Prévia da selfie" />
          </div>

          <button @click="registrarPonto" class="btn-confirmar" :disabled="loading">
            {{ loading ? 'Registrando...' : 'Registrar Ponto' }}
          </button>

          <p v-if="message" :class="['msg', success ? 'msg-sucesso' : 'msg-erro']">
            {{ message }}
          </p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { pontoService } from '@/services/pontoService';

export default {
  name: 'Dashboard',
  data() {
    return {
      mostrarOpcoes: false,
      tipo: '',
      observacao: '',
      selfie: null,
      previewSelfie: null,
      showCamera: false,
      stream: null,
      contador: 0, // contador regressivo
      loading: false,
      message: '',
      success: false,
      statusAtual: null,
      entradaDoDia: null
    };
  },
  computed: {
    statusClass() {
      if (!this.statusAtual) return '';
      const s = this.statusAtual.toLowerCase();
      if (s.includes('trabalhando')) return 'status-trabalhando';
      if (s.includes('pausa')) return 'status-pausa';
      if (s.includes('saida') || s.includes('inativo')) return 'status-inativo';
      return 'status-default';
    }
  },
  watch: {
    mostrarOpcoes(novoValor) {
      if (!novoValor) {
        this.message = '';
        this.success = false;
        this.resetForm();
      }
    }
  },
  methods: {
    isAdmin() {
      if (this.$store.state.user?.is_admin) this.$router.replace('/admin/dashboard');
    },

    async abrirCamera() {
      this.showCamera = true;
      await this.$nextTick();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        this.stream = stream;
        this.$refs.video.srcObject = stream;
      } catch (e) {
        alert('Erro ao acessar a câmera: ' + e.message);
        this.showCamera = false;
      }
    },

    async iniciarContagemEselfie() {
      this.contador = 5;
      while (this.contador > 0) {
        await new Promise(r => setTimeout(r, 1000));
        this.contador--;
      }
      await this.capturarSelfieAutomatica();
    },

    async capturarSelfieAutomatica() {
      const video = this.$refs.video;
      if (!video) throw new Error('Câmera não está ativa.');
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          const selfieFile = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
          this.selfie = selfieFile;
          this.previewSelfie = URL.createObjectURL(blob);
          this.fecharCamera();
          resolve();
        });
      });
    },

    fecharCamera() {
      if (this.stream) this.stream.getTracks().forEach(t => t.stop());
      this.stream = null;
      this.showCamera = false;
      this.contador = 0;
    },

    async registrarPonto() {
      if (!this.tipo) return this.setMsg('Selecione o tipo de ponto.', false);

      try {
        // Abre câmera e faz contagem regressiva
        await this.abrirCamera();
        await this.iniciarContagemEselfie();
      } catch (e) {
        return this.setMsg('Erro ao capturar selfie: ' + e.message, false);
      }

      if (!this.selfie) return this.setMsg('Não foi possível capturar a selfie.', false);

      this.loading = true;
      this.message = '';
      try {
        const pos = await this.getLocation();
        await pontoService.registrar({
          tipo: this.tipo,
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          observacao: this.observacao,
          selfie: this.selfie
        });
        this.setMsg('Ponto registrado com sucesso!', true);
        this.resetForm();
        this.buscarStatus();
        this.buscarEntradaDoDia();
        this.mostrarOpcoes = false;
      } catch (e) {
        this.setMsg(e.response?.data?.detail || 'Erro, verifique as configurações de localização do seu dispositivo.', false);
      } finally {
        this.loading = false;
      }
    },

    setMsg(msg, ok) {
      this.message = msg;
      this.success = ok;
    },

    getLocation() {
      return new Promise((res, rej) => {
        if (!navigator.geolocation) rej(new Error('Geolocalização não suportada.'));
        navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 10000 });
      });
    },

    resetForm() {
      this.tipo = '';
      this.observacao = '';
      this.selfie = null;
      this.previewSelfie = null;
    },

    async buscarStatus() {
      try {
        const r = await pontoService.getStatus();
        // console.log(r.data);
        this.statusAtual = r.data?.status || r.data;
      } catch {
        this.statusAtual = 'Indisponível';
      }
    },

    async buscarEntradaDoDia() {
      try {
        const r = await pontoService.getEntradaDoDia();
        // console.log(r.data);
        this.entradaDoDia = r.data ? new Date(r.data).toLocaleString('pt-BR') : 'Indisponível';
      } catch {
        this.entradaDoDia = null;
      }
    }
  },
  mounted() {
    this.isAdmin();
    this.buscarStatus();
    this.buscarEntradaDoDia();
  }
};
</script>


<style scoped>

/* contador central sobre o vídeo */
.contador {
  position: absolute;
  color: white;
  font-size: 3rem;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.5);
  padding: 1rem 2rem;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 1s infinite ease-in-out;
}
@keyframes pulse {
  0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(1.4); opacity: 0.6; }
}
/* --- estrutura geral permanece igual --- */
.dashboard-mobile {
  max-width: 480px;
  margin: 0 auto;
  padding: 1.5rem 1rem;
  background: #f8fafc;
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .dashboard-mobile {
    max-width: 600px;
    padding: 2rem 2.5rem;
    border-radius: 16px;
    background: #f9fafb;
  }
}

/* Cabeçalho */
.header {
  text-align: center;
  margin-bottom: 1rem;
}
.titulo {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1e293b;
}

/* Status */
.status-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 1rem 1.2rem;
  margin-bottom: 1.5rem;
}
.status-info {
  display: flex;
  justify-content: space-between;
  font-size: 0.95rem;
  margin-bottom: 0.4rem;
}
.label { color: #64748b; }
.valor { font-weight: 600; }
.status-trabalhando { color: #16a34a; }
.status-pausa { color: #eab308; }
.status-inativo { color: #dc2626; }
.status-default { color: #334155; }

/* Botão principal */
.btn-principal {
  width: 100%;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
}
.btn-principal:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(59, 130, 246, 0.4);
}

/* --- MODAL --- */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 1rem;
}
.modal-content {
  background: #fff;
  border-radius: 16px;
  padding: 1.5rem;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  position: relative;
  animation: scaleIn 0.25s ease;
}
.modal-title {
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
}
.modal-close {
  position: absolute;
  top: 12px;
  right: 14px;
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #64748b;
}
.modal-close:hover { color: #1e293b; }

/* Fade animation */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from, .fade-leave-to { opacity: 0; }
@keyframes scaleIn {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

/* Reaproveita seus estilos existentes de campo, botão, etc */
.campo label { font-weight: 500; font-size: 0.9rem; color: #475569; margin-bottom: 0.3rem; display: block; }
.input { width: 100%; border: 1px solid #cbd5e1; border-radius: 10px; padding: 0.75rem; font-size: 1rem; }
.input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59,130,246,0.15); }
.btn-secundario, .btn-capturar, .btn-confirmar {
  background-color: #10b981;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.8rem;
  font-weight: 600;
  width: 100%;
  transition: background-color 0.2s, transform 0.1s;
}
.btn-secundario:hover, .btn-capturar:hover, .btn-confirmar:hover {
  background-color: #059669;
  transform: translateY(-1px);
}
.camera-box {
  position: relative;
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}
.camera-box video {
  width: 100%;
  max-height: 220px; /* ✅ reduz a altura no mobile */
  border-radius: 10px;
  object-fit: cover;
  background: #000;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
}
/* .camera-box video { width: 100%; border-radius: 10px; margin-top: 0.5rem; } */
/* .preview img { margin-top: 0.5rem; width: 100%; border-radius: 10px; } */
.preview img {
  margin-top: 0.75rem;
  width: 100%;
  max-height: 220px; /* ✅ mantém consistência com o vídeo */
  object-fit: cover;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}
.msg { text-align: center; padding: 0.8rem; border-radius: 8px; font-weight: 500; font-size: 0.95rem; }
.msg-sucesso { background: #dcfce7; color: #166534; }
.msg-erro { background: #fee2e2; color: #991b1b; }

@media (max-width: 480px) {
  .camera-box video,
  .preview img {
    max-height: 180px; /* ✅ ainda menor no mobile estreito */
  }

  .btn-capturar {
    width: 100%;
  }
}
</style>
